import { useEffect, useState, useRef } from 'react';
import { Workout, WorkoutDataPoint } from '../types/workout';
import {
  sendErgCommand,
  getTrainerControlChar,
  onPowerUpdate,
  onHeartRateUpdate,
  onCadenceUpdate,
} from '../lib/bluetooth';
import { expandWorkoutToIntervals } from '../lib/workoutUtils';
import {
  requestWakeLock,
  releaseWakeLock,
  handleVisibilityChange,
} from '../lib/screenWakeLock';
import { stravaService } from '../lib/strava';
import { StravaSettings } from '../lib/stravaSettings';

interface WorkoutRunnerProps {
  workout: Workout;
  onReset: () => void;
  isHeartRateConnected: boolean;
  isCadenceEnabled: boolean;
  isStravaConnected: boolean;
  stravaSettings: StravaSettings;
}

export function WorkoutRunner({
  workout,
  onReset,
  isHeartRateConnected,
  isCadenceEnabled,
  isStravaConnected,
  stravaSettings,
}: WorkoutRunnerProps) {
  // Expand the workout into individual intervals for execution
  const allIntervals = expandWorkoutToIntervals(workout);

  const [timeLeft, setTimeLeft] = useState(allIntervals[0]?.duration || 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPower, setCurrentPower] = useState<number | null>(null);
  const [currentHeartRate, setCurrentHeartRate] = useState<number | null>(null);
  const [currentCadence, setCurrentCadence] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [workoutData, setWorkoutData] = useState<WorkoutDataPoint[]>([]);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [isUploadingToStrava, setIsUploadingToStrava] = useState(false);
  const [stravaUploadStatus, setStravaUploadStatus] = useState<{
    success: boolean;
    activityId?: number;
    error?: string;
  } | null>(null);
  const [showWorkoutOverview, setShowWorkoutOverview] = useState(true);

  // Use refs to track current state in callbacks
  const isCompletedRef = useRef(false);
  const isPausedRef = useRef(false);
  const currentIndexRef = useRef(0);
  const currentPowerRef = useRef<number | null>(null);
  const currentHeartRateRef = useRef<number | null>(null);
  const currentCadenceRef = useRef<number | null>(null);

  // Update refs when state changes
  useEffect(() => {
    isCompletedRef.current = isCompleted;
  }, [isCompleted]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    currentPowerRef.current = currentPower;
  }, [currentPower]);

  useEffect(() => {
    currentHeartRateRef.current = currentHeartRate;
  }, [currentHeartRate]);

  useEffect(() => {
    currentCadenceRef.current = currentCadence;
  }, [currentCadence]);

  // Calculate total workout times
  const totalWorkoutTime = allIntervals.reduce(
    (sum, interval) => sum + interval.duration,
    0
  );
  const getElapsedTime = () => {
    let elapsed = 0;
    for (let i = 0; i < currentIndex; i++) {
      elapsed += allIntervals[i].duration;
    }
    elapsed += (allIntervals[currentIndex]?.duration || 0) - timeLeft;
    return elapsed;
  };
  const getTotalTimeRemaining = () => {
    let remaining = timeLeft; // Current interval remaining time
    for (let i = currentIndex + 1; i < allIntervals.length; i++) {
      remaining += allIntervals[i].duration;
    }
    return remaining;
  };

  // Helper function to get the current section for a given interval index
  const getCurrentSection = (intervalIndex: number) => {
    let intervalCount = 0;
    for (const section of workout.sections) {
      if (section.type === 'block' || section.type === 'ramp') {
        if (intervalIndex === intervalCount) {
          return section;
        }
        intervalCount += 1;
      } else if (section.type === 'interval') {
        const sectionTotalIntervals = section.repeats * 2;
        if (intervalIndex >= intervalCount && intervalIndex < intervalCount + sectionTotalIntervals) {
          return section;
        }
        intervalCount += sectionTotalIntervals;
      }
    }
    return null;
  };

  // Calculate current target power for ramps
  const getCurrentTargetPower = () => {
    const currentSection = getCurrentSection(currentIndex);
    if (currentSection?.type === 'ramp' && allIntervals[currentIndex]) {
      const rampSection = currentSection;
      const currentInterval = allIntervals[currentIndex];
      const powerDifference = rampSection.endPower - rampSection.startPower;
      const powerPerSecond = powerDifference / currentInterval.duration;
      const secondsElapsed = currentInterval.duration - timeLeft;
      return Math.round(rampSection.startPower + (powerPerSecond * secondsElapsed));
    }
    return allIntervals[currentIndex]?.targetPower || 0;
  };

  // Get workout overview with section completion status
  const getWorkoutOverview = () => {
    let intervalCount = 0;
    const sections = workout.sections.map((section, sectionIdx) => {
      let sectionIntervals = 0;
      let status: 'completed' | 'current' | 'upcoming' = 'upcoming';
      
      if (section.type === 'block' || section.type === 'ramp') {
        sectionIntervals = 1;
        if (currentIndex === intervalCount) {
          status = 'current';
        } else if (currentIndex > intervalCount) {
          status = 'completed';
        }
      } else if (section.type === 'interval') {
        sectionIntervals = section.repeats * 2;
        if (currentIndex >= intervalCount && currentIndex < intervalCount + sectionIntervals) {
          status = 'current';
        } else if (currentIndex >= intervalCount + sectionIntervals) {
          status = 'completed';
        }
      }

      const result = {
        ...section,
        status,
        sectionIndex: sectionIdx,
        intervalStart: intervalCount,
        intervalCount: sectionIntervals,
      };
      
      intervalCount += sectionIntervals;
      return result;
    });

    return sections;
  };

  // Record workout data when power, heart rate, or cadence updates
  const recordDataPoint = () => {
    if (!isPausedRef.current && !isCompletedRef.current) {
      const currentTargetPower = getCurrentTargetPower();
      const dataPoint: WorkoutDataPoint = {
        timestamp: Date.now(),
        power: currentPowerRef.current || 0,
        targetPower: currentTargetPower,
        heartRate: currentHeartRateRef.current || undefined,
        cadence: currentCadenceRef.current || undefined,
      };

      setWorkoutData((prev) => [...prev, dataPoint]);
    }
  };

  // Determine which section/repeat we're currently in for display
  const getCurrentSectionInfo = () => {
    let intervalCount = 0;

    for (
      let sectionIdx = 0;
      sectionIdx < workout.sections.length;
      sectionIdx++
    ) {
      const section = workout.sections[sectionIdx];

      if (section.type === 'block') {
        // Single block = 1 interval
        if (currentIndex === intervalCount) {
          return {
            type: 'block',
            name: section.name,
            progress: null,
            phase: null,
          };
        }
        intervalCount += 1;
      } else if (section.type === 'interval') {
        // Interval set = repeats * 2 (on + rest)
        const sectionTotalIntervals = section.repeats * 2;

        if (
          currentIndex >= intervalCount &&
          currentIndex < intervalCount + sectionTotalIntervals
        ) {
          const intervalInSection = currentIndex - intervalCount;
          const repeatIndex = Math.floor(intervalInSection / 2);
          const isOnInterval = intervalInSection % 2 === 0;

          return {
            type: 'interval',
            name: section.name,
            progress: `${repeatIndex + 1}/${section.repeats}`,
            phase: isOnInterval ? 'Work' : 'Rest',
          };
        }
        intervalCount += sectionTotalIntervals;
      } else if (section.type === 'ramp') {
        // Ramp section = 1 interval
        if (currentIndex === intervalCount) {
          return {
            type: 'ramp',
            name: section.name,
            progress: null,
            phase: 'Ramp',
          };
        }
        intervalCount += 1;
      }
    }

    return { type: 'unknown', name: 'Unknown', progress: null, phase: null };
  };

  useEffect(() => {
    let isCancelled = false;
    setStartTime(Date.now());

    // Request wake lock when workout starts
    const initializeWakeLock = async () => {
      await requestWakeLock();
    };
    initializeWakeLock();

    // Set up visibility change handler to re-acquire wake lock
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Prevent accidental page closing during workout or upload
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isCompleted || isUploadingToStrava) {
        e.preventDefault();
        if (isUploadingToStrava) {
          e.returnValue =
            'Your workout is being uploaded to Strava. Are you sure you want to leave? The upload will be cancelled.';
          return 'Your workout is being uploaded to Strava. Are you sure you want to leave? The upload will be cancelled.';
        } else {
          e.returnValue =
            'You have an active workout in progress. Are you sure you want to leave? Your workout data will be lost.';
          return 'You have an active workout in progress. Are you sure you want to leave? Your workout data will be lost.';
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    const runWorkout = async () => {
      const char = await getTrainerControlChar();

      for (let i = currentIndex; i < allIntervals.length; i++) {
        if (isCancelled) return;

        const { targetPower, duration } = allIntervals[i];
        const currentSection = getCurrentSection(i);

        setCurrentIndex(i);
        setTimeLeft(duration);

        // Handle ramp sections with gradual power increase
        if (currentSection?.type === 'ramp') {
          const rampSection = currentSection;
          const powerDifference = rampSection.endPower - rampSection.startPower;
          const powerPerSecond = powerDifference / duration;

          for (let t = duration; t > 0; t--) {
            if (isCancelled) return;

            // Wait for unpause if paused
            while (isPaused && !isCancelled) {
              await new Promise((res) => setTimeout(res, 100));
            }

            if (isCancelled) return;

            // Calculate current power for this second in the ramp
            const secondsElapsed = duration - t;
            const currentRampPower = Math.round(rampSection.startPower + (powerPerSecond * secondsElapsed));

            try {
              await sendErgCommand(char, currentRampPower);
            } catch (error) {
              console.warn('ERG command failed (workout continuing):', error);
            }

            setTimeLeft(t);
            await new Promise((res) => setTimeout(res, 1000));
          }
        } else {
          // Handle normal blocks and intervals
          try {
            await sendErgCommand(char, targetPower || 0);
          } catch (error) {
            console.warn('ERG command failed (workout continuing):', error);
            // Continue workout even if ERG command fails
          }

          for (let t = duration; t > 0; t--) {
            if (isCancelled) return;

            // Wait for unpause if paused
            while (isPaused && !isCancelled) {
              await new Promise((res) => setTimeout(res, 100));
            }

            if (isCancelled) return;
            setTimeLeft(t);
            await new Promise((res) => setTimeout(res, 1000));
          }
        }
      }

      if (!isCancelled) {
        setIsCompleted(true);
      }
    };

    runWorkout();

    // Set up power data recording
    const cleanupPower = onPowerUpdate((watts) => {
      setCurrentPower(watts);
      // Trigger data recording when power updates
      setTimeout(recordDataPoint, 0);
    });

    // Set up heart rate data recording if connected
    let cleanupHeartRate: (() => void) | null = null;
    if (isHeartRateConnected) {
      cleanupHeartRate = onHeartRateUpdate((bpm) => {
        setCurrentHeartRate(bpm);
        // Trigger data recording when heart rate updates
        setTimeout(recordDataPoint, 0);
      });
    }

    // Set up cadence data recording if enabled
    let cleanupCadence: (() => void) | null = null;
    if (isCadenceEnabled) {
      cleanupCadence = onCadenceUpdate((rpm) => {
        setCurrentCadence(rpm);
        // Trigger data recording when cadence updates
        setTimeout(recordDataPoint, 0);
      });
    }

    return () => {
      isCancelled = true;
      cleanupPower();
      if (cleanupHeartRate) cleanupHeartRate();
      if (cleanupCadence) cleanupCadence();
      // Release wake lock when workout component unmounts
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Remove beforeunload warning when workout ends
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    isPaused,
    currentIndex,
    isCompleted,
    isHeartRateConnected,
    isCadenceEnabled,
    isStravaConnected,
    isUploadingToStrava,
  ]);

  // Handle auto-upload when workout completes
  useEffect(() => {
    if (
      isCompleted &&
      isStravaConnected &&
      stravaSettings.autoUpload &&
      !isUploadingToStrava &&
      !stravaUploadStatus
    ) {
      setIsUploadingToStrava(true);
      uploadToStrava();
    }
  }, [isCompleted, isStravaConnected, stravaSettings.autoUpload]);

  const uploadToStrava = async () => {
    if (!isStravaConnected || workoutData.length === 0) return;

    try {
      // Only set uploading state if not already set (for manual uploads)
      if (!isUploadingToStrava) {
        setIsUploadingToStrava(true);
      }
      setStravaUploadStatus(null);

      const accessToken = await stravaService.getAccessToken();
      if (!accessToken) {
        throw new Error('Not authenticated with Strava');
      }

      const workoutName = `Indoor Cycling - ${new Date(startTime).toLocaleDateString()}`;
      const activity = await stravaService.createActivity(
        accessToken,
        workoutData,
        startTime,
        totalWorkoutTime,
        workoutName,
        workout,
        stravaSettings.autoUploadPhoto
      );

      setStravaUploadStatus({
        success: true,
        activityId: activity.id,
      });
    } catch (error) {
      console.error('Failed to upload to Strava:', error);
      setStravaUploadStatus({
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      });
    } finally {
      setIsUploadingToStrava(false);
    }
  };

  const sectionInfo = getCurrentSectionInfo();
  const currentInterval = allIntervals[currentIndex];

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const generateTCXFile = () => {
    const powerData = workoutData.filter((d) => d.power > 0);
    const hrData = workoutData.filter((d) => d.heartRate && d.heartRate > 0);
    const cadenceData = workoutData.filter((d) => d.cadence && d.cadence > 0);

    const avgPower =
      powerData.length > 0
        ? powerData.reduce((sum, d) => sum + d.power, 0) / powerData.length
        : 0;
    const maxPower =
      powerData.length > 0 ? Math.max(...powerData.map((d) => d.power)) : 0;
    const avgHeartRate =
      hrData.length > 0
        ? hrData.reduce((sum, d) => sum + (d.heartRate || 0), 0) / hrData.length
        : 0;
    const maxHeartRate =
      hrData.length > 0 ? Math.max(...hrData.map((d) => d.heartRate || 0)) : 0;
    const avgCadence =
      cadenceData.length > 0
        ? cadenceData.reduce((sum, d) => sum + (d.cadence || 0), 0) /
          cadenceData.length
        : 0;
    const maxCadence =
      cadenceData.length > 0
        ? Math.max(...cadenceData.map((d) => d.cadence || 0))
        : 0;

    const tcxData = `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2">
  <Activities>
    <Activity Sport="Biking">
      <Id>${new Date(startTime).toISOString()}</Id>
      <Notes>Indoor cycling workout with ERG trainer</Notes>
      <Lap StartTime="${new Date(startTime).toISOString()}">
        <TotalTimeSeconds>${workoutData.length}</TotalTimeSeconds>
        <MaximumSpeed>0</MaximumSpeed>
        <Calories>0</Calories>
        ${avgHeartRate > 0 ? `<AverageHeartRateBpm><Value>${Math.round(avgHeartRate)}</Value></AverageHeartRateBpm>` : ''}
        ${maxHeartRate > 0 ? `<MaximumHeartRateBpm><Value>${maxHeartRate}</Value></MaximumHeartRateBpm>` : ''}
        <Extensions>
          <LX xmlns="http://www.garmin.com/xmlschemas/ActivityExtension/v2">
            <AvgPower>${Math.round(avgPower)}</AvgPower>
            <MaxPower>${maxPower}</MaxPower>
            ${avgCadence > 0 ? `<AvgCadence>${Math.round(avgCadence)}</AvgCadence>` : ''}
            ${maxCadence > 0 ? `<MaxCadence>${maxCadence}</MaxCadence>` : ''}
          </LX>
        </Extensions>
        <Track>
          ${workoutData
            .map(
              (point) => `
          <Trackpoint>
            <Time>${new Date(point.timestamp).toISOString()}</Time>
            ${point.heartRate ? `<HeartRateBpm><Value>${point.heartRate}</Value></HeartRateBpm>` : ''}
            <Extensions>
              <TPX xmlns="http://www.garmin.com/xmlschemas/ActivityExtension/v2">
                <Watts>${point.power}</Watts>
                ${point.cadence ? `<Cadence>${point.cadence}</Cadence>` : ''}
              </TPX>
            </Extensions>
          </Trackpoint>`
            )
            .join('')}
        </Track>
      </Lap>
    </Activity>
  </Activities>
</TrainingCenterDatabase>`;

    const blob = new Blob([tcxData], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workout-${new Date(startTime).toISOString().split('T')[0]}.tcx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getWorkoutStats = () => {
    const powerData = workoutData.filter((d) => d.power > 0);
    const hrData = workoutData.filter((d) => d.heartRate && d.heartRate > 0);
    const cadenceData = workoutData.filter((d) => d.cadence && d.cadence > 0);

    return {
      avgPower:
        powerData.length > 0
          ? Math.round(
              powerData.reduce((sum, d) => sum + d.power, 0) / powerData.length
            )
          : 0,
      maxPower:
        powerData.length > 0 ? Math.max(...powerData.map((d) => d.power)) : 0,
      avgHeartRate:
        hrData.length > 0
          ? Math.round(
              hrData.reduce((sum, d) => sum + (d.heartRate || 0), 0) /
                hrData.length
            )
          : 0,
      maxHeartRate:
        hrData.length > 0
          ? Math.max(...hrData.map((d) => d.heartRate || 0))
          : 0,
      avgCadence:
        cadenceData.length > 0
          ? Math.round(
              cadenceData.reduce((sum, d) => sum + (d.cadence || 0), 0) /
                cadenceData.length
            )
          : 0,
      maxCadence:
        cadenceData.length > 0
          ? Math.max(...cadenceData.map((d) => d.cadence || 0))
          : 0,
      dataPoints: workoutData.length,
    };
  };

  const stats = getWorkoutStats();

  return (
    <div className="bg-white p-4 rounded shadow-lg">
      {isCompleted ? (
        // Workout Complete Screen
        <div className="text-center space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-green-600 mb-2">
              🎉 Workout Complete!
            </h2>
            <p className="text-lg text-gray-600">
              Great job! You completed your {formatTime(totalWorkoutTime)}{' '}
              workout.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-800">Duration</h3>
              <p className="text-2xl font-bold text-blue-600">
                {formatTime(totalWorkoutTime)}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded">
              <h3 className="font-semibold text-green-800">Avg Power</h3>
              <p className="text-2xl font-bold text-green-600">
                {stats.avgPower} W
              </p>
              <p className="text-sm text-green-600">Max: {stats.maxPower} W</p>
            </div>
            {isHeartRateConnected && stats.avgHeartRate > 0 && (
              <div className="bg-red-50 p-4 rounded">
                <h3 className="font-semibold text-red-800">Avg HR</h3>
                <p className="text-2xl font-bold text-red-600">
                  {stats.avgHeartRate} bpm
                </p>
                <p className="text-sm text-red-600">
                  Max: {stats.maxHeartRate} bpm
                </p>
              </div>
            )}
            {isCadenceEnabled && stats.avgCadence > 0 && (
              <div className="bg-purple-50 p-4 rounded">
                <h3 className="font-semibold text-purple-800">Avg Cadence</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.avgCadence} rpm
                </p>
                <p className="text-sm text-purple-600">
                  Max: {stats.maxCadence} rpm
                </p>
              </div>
            )}
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="font-semibold text-gray-800">Data Points</h3>
              <p className="text-2xl font-bold text-gray-600">
                {stats.dataPoints}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Export Your Workout</h3>

            {/* Strava Upload Section */}
            {isStravaConnected && (
              <div className="bg-orange-50 p-4 rounded border border-orange-200">
                {isUploadingToStrava ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                    <div className="text-center">
                      <div className="text-orange-700 font-medium">
                        Uploading to Strava...
                      </div>
                      <div className="text-orange-600 text-xs mt-1">
                        Processing workout file with all data points
                      </div>
                    </div>
                  </div>
                ) : stravaUploadStatus?.success ? (
                  <div className="text-center">
                    <div className="text-green-600 font-medium mb-2">
                      ✅ Successfully uploaded to Strava!
                    </div>
                    <a
                      href={`https://www.strava.com/activities/${stravaUploadStatus.activityId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:text-orange-700 underline text-sm"
                    >
                      View on Strava →
                    </a>
                  </div>
                ) : stravaUploadStatus?.error ? (
                  <div className="text-center">
                    <div className="text-red-600 font-medium mb-2">
                      ❌ Strava upload failed
                    </div>
                    <div className="text-red-600 text-sm mb-2">
                      {stravaUploadStatus.error}
                    </div>
                    <button
                      onClick={uploadToStrava}
                      className="px-4 py-2 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                    >
                      Retry Upload
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-orange-700 font-medium mb-2">
                      🚴‍♂️ Ready to upload to Strava
                    </div>
                    <button
                      onClick={uploadToStrava}
                      className="px-4 py-2 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                    >
                      Upload to Strava
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={generateTCXFile}
                className="px-6 py-3 bg-blue-500 text-white rounded font-medium hover:bg-blue-600"
              >
                📁 Download TCX File
              </button>
            </div>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              TCX files can be uploaded to Strava, TrainingPeaks, Garmin
              Connect, and other fitness platforms.
              {isHeartRateConnected || isCadenceEnabled
                ? ` Includes ${[
                    'power',
                    isHeartRateConnected ? 'heart rate' : '',
                    isCadenceEnabled ? 'cadence' : '',
                  ]
                    .filter(Boolean)
                    .join(', ')} data.`
                : ' Power data only.'}
            </p>
          </div>

          <button
            onClick={onReset}
            className="px-6 py-3 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Start New Workout
          </button>
        </div>
      ) : (
        // Running Workout Screen - COMPACT DESIGN
        <>
          {/* Header Row */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="text-2xl">🚴‍♂️</span>
                {sectionInfo.name}
                {isPaused && (
                  <span className="text-yellow-600 text-sm">(Paused)</span>
                )}
              </h2>
              <div className="text-sm text-gray-600">
                {sectionInfo.type === 'block' ? 'Block' : sectionInfo.type === 'ramp' ? 'Ramp' : 'Interval Set'}
                {sectionInfo.phase && ` - ${sectionInfo.phase}`}
                {sectionInfo.progress && ` (${sectionInfo.progress})`}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsPaused(!isPaused)}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  isPaused
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
              >
                {isPaused ? '▶️ Resume' : '⏸️ Pause'}
              </button>
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to stop the workout? Your progress will be saved, but you won't be able to continue this session."
                    )
                  ) {
                    setIsCompleted(true);
                  }
                }}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
              >
                🛑 Stop
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>
                Step {currentIndex + 1} of {allIntervals.length}
              </span>
              <span>
                {Math.round(((currentIndex + 1) / allIntervals.length) * 100)}%
                complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                style={{
                  width: `${((currentIndex + 1) / allIntervals.length) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Workout Overview */}
          <div className="mb-4 bg-gray-50 rounded">
            <button
              onClick={() => setShowWorkoutOverview(!showWorkoutOverview)}
              className="w-full p-3 text-left flex items-center justify-between hover:bg-gray-100 rounded"
            >
              <div className="text-xs font-medium text-gray-700">WORKOUT OVERVIEW</div>
              <span className="text-gray-500 text-xs">
                {showWorkoutOverview ? '▼' : '▶'}
              </span>
            </button>
            {showWorkoutOverview && (
              <div className="px-3 pb-3">
                <div className="flex flex-wrap gap-2">
                  {getWorkoutOverview().map((section, idx) => {
                const isBlock = section.type === 'block';
                const isRamp = section.type === 'ramp';
                const isInterval = section.type === 'interval';
                
                let powerText = '';
                let sectionDuration = 0;
                
                if (isBlock) {
                  powerText = `${section.targetPower}W`;
                  sectionDuration = section.duration;
                } else if (isRamp) {
                  powerText = `${section.startPower}-${section.endPower}W`;
                  sectionDuration = section.duration;
                } else if (isInterval) {
                  powerText = `${section.onInterval.targetPower}W×${section.repeats}`;
                  sectionDuration = (section.onInterval.duration + section.restInterval.duration) * section.repeats;
                }

                const statusColors = {
                  completed: 'bg-green-100 text-green-800 border-green-200',
                  current: 'bg-blue-100 text-blue-800 border-blue-200 ring-2 ring-blue-300',
                  upcoming: 'bg-gray-100 text-gray-600 border-gray-200'
                };

                return (
                  <div
                    key={idx}
                    className={`px-2 py-1 rounded text-xs border ${statusColors[section.status]} transition-all duration-300`}
                  >
                    <div className="font-medium flex items-center gap-1">
                      {section.status === 'completed' && <span className="text-green-600">✓</span>}
                      {section.status === 'current' && <span className="text-blue-600">▶</span>}
                      {section.name}
                    </div>
                    <div className="text-xs opacity-75">
                      {powerText} • {formatTime(sectionDuration)}
                      {isInterval && ` • ${section.repeats}×`}
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>

          {/* Main Data Row - Compact horizontal layout */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {/* Target Power */}
            <div className="bg-green-50 p-3 rounded text-center">
              <div className="text-xs text-green-700 font-medium">TARGET</div>
              <div className="text-lg font-bold text-green-600">
                {getCurrentTargetPower()}W
              </div>
            </div>

            {/* Current Power */}
            <div className="bg-blue-50 p-3 rounded text-center">
              <div className="text-xs text-blue-700 font-medium">POWER</div>
              <div className="text-lg font-bold text-blue-600">
                {currentPower !== null ? `${currentPower}W` : 'N/A'}
              </div>
              {currentPower !== null && currentInterval && (
                <div className="text-xs text-gray-600">
                  {currentPower > getCurrentTargetPower() ? '+' : ''}
                  {currentPower - getCurrentTargetPower()}W
                </div>
              )}
            </div>

            {/* Heart Rate */}
            {isHeartRateConnected && (
              <div className="bg-red-50 p-3 rounded text-center">
                <div className="text-xs text-red-700 font-medium">
                  HEART RATE
                </div>
                <div className="text-lg font-bold text-red-600">
                  {currentHeartRate !== null ? `${currentHeartRate}` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">bpm</div>
              </div>
            )}

            {/* Cadence */}
            {isCadenceEnabled && (
              <div className="bg-purple-50 p-3 rounded text-center">
                <div className="text-xs text-purple-700 font-medium">
                  CADENCE
                </div>
                <div className="text-lg font-bold text-purple-600">
                  {currentCadence !== null ? `${currentCadence}` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">rpm</div>
              </div>
            )}

            {/* Current Interval Time */}
            <div className="bg-orange-50 p-3 rounded text-center">
              <div className="text-xs text-orange-700 font-medium">
                INTERVAL
              </div>
              <div className="text-lg font-bold text-orange-600">
                {formatTime(timeLeft)}
              </div>
              <div className="text-xs text-gray-600">remaining</div>
            </div>

            {/* Total Time */}
            <div className="bg-indigo-50 p-3 rounded text-center">
              <div className="text-xs text-indigo-700 font-medium">TOTAL</div>
              <div className="text-lg font-bold text-indigo-600">
                {formatTime(getTotalTimeRemaining())}
              </div>
              <div className="text-xs text-gray-600">remaining</div>
            </div>
          </div>

          {/* Compact time summary */}
          <div className="bg-gray-50 p-3 rounded">
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                Elapsed:{' '}
                <span className="font-medium">
                  {formatTime(getElapsedTime())}
                </span>
              </span>
              <span>
                Total:{' '}
                <span className="font-medium">
                  {formatTime(totalWorkoutTime)}
                </span>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
